import { AsyncPipe, NgClass } from '@angular/common';
import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { Store } from '@ngrx/store';
import dayjs from 'dayjs';
import { NgScrollbar } from 'ngx-scrollbar';
import { filter, takeUntil } from 'rxjs/operators';
import { DeepCopyService } from '../../../services/deep-copy.service';
import { AppState } from '../../../store/app-state';
import {
  createOrUpdateDraft,
  loadSingleProfilePicture,
  messagesActions,
  setMessage,
} from '../../../store/messages/messages.actions';
import {
  IMessage,
  IMessageThread,
  IMessagingUser,
} from '../../../store/messages/messages.interfaces';
import {
  getMessagingLoading,
  getThreadDraft,
  getThreadMessages,
} from '../../../store/messages/messages.selectors';
import { RichTextEditorComponent } from '../../inputs/rich-text-editor/rich-text-editor.component';
import { DiscussionViewCommonComponent } from '../discussion-view-common/discussion-view-common.component';
import { MessageItemComponent } from '../message-item/message-item.component';
import { SimpleButtonComponent } from '@app/framework/buttons/simple-medium-button/simple-button.component';

@Component({
  selector: 'app-discussion-thread-view',
  templateUrl: './discussion-thread-view.component.html',
  styleUrls: ['./discussion-thread-view.component.scss'],
  imports: [
    NgScrollbar,
    MessageItemComponent,
    RichTextEditorComponent,
    AsyncPipe,
    NgClass,
    SimpleButtonComponent,
  ],
})
export class DiscussionThreadViewComponent
  extends DiscussionViewCommonComponent
  implements OnInit, OnDestroy
{
  @ViewChild('scrollbar') scrollbar: NgScrollbar;
  threadMessages: IMessageThread;
  isLoading$ = this.store.select(getMessagingLoading);

  constructor(protected store: Store<AppState>) {
    super();
  }

  ngOnInit(): void {
    super.handleUpload();
    super.handleChatMembers();
    super.handleDraft(getThreadDraft);
    super.handleSaveEvent();

    this.handleLoadThreadMessages();
    this.setReadStatus();
  }

  saveDraft(message: string) {
    this.store.dispatch(setMessage({ message }));
    this.store.dispatch(createOrUpdateDraft({ message }));
  }

  private handleLoadThreadMessages() {
    this.store
      .select(getThreadMessages)
      .pipe(
        takeUntil(this.isDestroyed$),
        filter((data) => !!data?.replies || !!data?.mainMessage),
      )
      .subscribe((threadMessages) => {
        const threadMessagesCopy = DeepCopyService.deepCopy(threadMessages);
        this.sortMessages(threadMessagesCopy);
        threadMessagesCopy.replies = threadMessagesCopy.replies.reverse();
        this.threadMessages = threadMessagesCopy;
        this.handleProfilePictures();
        if (this.shouldScrollToBottom) {
          this.shouldScrollToBottom = false;
          setTimeout(() => this.scrollToBottom(300), 100);
        }
      });
  }

  private sortMessages(messages: IMessageThread) {
    messages.replies.sort((a, b) => {
      return dayjs(a.updated_at).isAfter(b.updated_at) ? -1 : 1;
    });
    return messages;
  }

  private scrollToBottom(duration = 200) {
    this.scrollbar?.scrollTo({ bottom: 0, duration });
    this.scrollbar?.update();
  }

  /**
   * Check if there are users which do not have a profile picture loaded and load if needed.
   * @param loadedUserIds
   * @private
   */
  private loadMissingProfilePictures(loadedUserIds: number[]) {
    // these users we have after adding more messages
    const updatedUserIds = [
      ...new Set(this.threadMessages.replies.map((message) => message.user?.id)),
    ];
    if (loadedUserIds.length !== updatedUserIds.length) {
      // load profile pictures not loaded yet.
      const diff = updatedUserIds.filter(
        (updatedUserId) => !loadedUserIds.find((loadedUserId) => loadedUserId === updatedUserId),
      );
      // if diff is not empty that means there are new users (which were not included in this.allMessages)
      // so we dispatch an action to load missing profile pics
      diff.forEach((userId) => {
        const user = this.threadMessages.replies.find((m) => m.user?.id === userId)?.user;
        this.store.dispatch(loadSingleProfilePicture({ user: user as IMessagingUser }));
      });
    }
  }

  private handleProfilePictures() {
    // these users were loaded in the past, and they have all the user data (with profile pic)
    const userIds = this.threadMessages?.replies?.map((m) => m.user?.id);
    if (!userIds?.length) {
      return;
    }
    userIds.push(this.threadMessages?.mainMessage?.id);
    const loadedUserIds = [...new Set(userIds)];
    this.loadMissingProfilePictures(loadedUserIds);
  }

  /**
   * set main message to read when thread view is opened or closed.
   * @private
   */
  private setReadStatus() {
    if (this.threadMessages?.mainMessage?.thread?.length > 0) {
      this.store.dispatch(
        messagesActions.updateMessageReadStatus({
          messageId: this.threadMessages.mainMessage.id,
          isRead: true,
        }),
      );
    }
  }

  trackByFn = (index: number, item: IMessage) => item.id;

  ngOnDestroy(): void {
    super.ngOnDestroy();
    this.setReadStatus();
  }
}
